<template>
  <div class="quick-action-before">
    <PhaseSection
      data-testid="quick-action-before-phase"
      :title="$t('quick_action.phases.before')"
      :description="$t('quick_action.before.specify_the_problem')"
      :isEditMode="isEditMode"
      :hasNextButton="hasNextButton"
      :hasBackButton="hasBackButton"
      @next="$emit('next')"
      @back="$emit('back')"
    >
      <MediaContainer
        data-private
        data-testid="quick-action-situation-media"
        data-tourid="quick-action-image-situation"
        id="situationPhoto"
        key="situation-image"
        slot="left"
        class="mt-6"
        :images="media"
        :readOnly="!isEditMode || isSaving"
        @change="handleSituationMediaChange"
      />
      <TranslationLoading slot="right" v-if="isTranslating" />
      <div slot="right" v-else>
        <p class="d-flex justify-end caption blue-grey--text mb-6">
          * {{ $t('common.required_fields') }}
        </p>
        <TextQuestion
          data-private
          data-testid="quick-action-situation"
          tourId="quick-action-textarea-situation"
          v-model="situation"
          :error="markRequiredFields && !situation"
          :question="`${$t('quick_action.before.whats_the_problem')}*`"
          :placeholder="$t('quick_action.before.whats_the_problem_placeholder')"
          :is-edit-mode="isEditMode"
          :maxLength="$constants.quickAction.problemMaxLength"
          rows="1"
          @focusOut="trackEvent('addSituation')"
        />

        <div :class="{ 'mt-0': isEditMode, 'mt-6': !isEditMode }">
          <CategoriesSelector
            data-private
            data-testid="quick-action-before-benefits"
            v-if="isEditMode"
            v-model="selectedCategories"
            :siteIds="[siteId]"
          />
          <ChipsList
            v-else
            translationKey="common.categories"
            :items="selectedCategories"
            :title="$t('common.category_other')"
          />
        </div>

        <div class="mt-6">
          <SituationTypesSelector
            data-private
            data-tourid="quickAction-select-typeOfSituation"
            v-if="isEditMode"
            v-model="selectedSituationTypes"
          />
          <ChipsList
            v-else
            translationKey="quick_action.situation_types.situations"
            :items="
              selectedSituationTypes.map(situation => situation.situation)
            "
            :title="$t('quick_action.before.type_of_situation')"
          />
        </div>
      </div>
    </PhaseSection>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'

import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import SituationTypesSelector from '@/components/SituationTypesSelector.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import ChipsList from '@/components/ChipsList.vue'

export default {
  name: 'Before',
  mixins: [mixin, trackerMixin],

  components: {
    PhaseSection,
    TextQuestion,
    CategoriesSelector,
    SituationTypesSelector,
    MediaContainer,
    TranslationLoading,
    ChipsList,
  },

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },

    rev: {
      type: Object,
      required: true,
    },

    isSaving: Boolean,
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
  },

  data() {
    return {
      organizationId: this.getOrganizationId(),
    }
  },

  computed: {
    selectedSituationTypes: {
      get() {
        return this.rev?.beforePhase?.situationTypes ?? []
      },
      set(situationTypes) {
        this.$emit('revChange')
        return this.updateSituationTypes(situationTypes)
      },
    },

    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        this.$emit('revChange')
        this.trackUpdate(this.rev, 'changeCategories')
        return this.updateCategories(categories)
      },
    },

    situation: {
      get() {
        return _.get(this.rev, 'beforePhase.situation', '')
      },
      set(situation) {
        this.$emit('revChange')
        this.updateSituation(situation)
      },
    },

    situationMedia() {
      return _.get(this.rev, 'beforePhase.media[0]', null)
    },

    media() {
      return this.rev?.beforePhase?.media ?? []
    },

    siteId() {
      return this.rev.location?.site?._id || this.rev.location?.site
    },
  },

  methods: {
    ...mapMutations({
      updateSituation: 'quickAction/updateSituation',
      updateSituationMedia: 'quickAction/updateSituationMedia',
      updateSituationTypes: 'quickAction/updateSituationTypes',
      updateCategories: 'quickAction/updateCategories',
    }),

    handleSituationMediaChange(media) {
      this.trackEvent('situationMediaPhoto')
      this.$emit('revChange')
      this.updateSituationMedia(media)
    },

    trackEvent: function(changedField) {
      this.trackUpdate(this.rev, changedField)
    },
  },
}
</script>

<style lang="scss" scoped>
.quick-action-before {
  display: flex;
  flex-direction: column;

  &__step-status {
    margin-bottom: 20px;
    padding: 0;
  }
}
</style>
